import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Tag, MapPin, Home, MessageCircle, ArrowUpRight, Video, Stethoscope } from "lucide-react";
import { DOCTOR, whatsappUrl, teleconsultUrl, TELECONSULT, CONTACT, BRAND } from "@/lib/constants";
import usePageTitle from "@/lib/usePageTitle";
import useOpenGraph from "@/lib/useOpenGraph";
import { coverForArticle } from "@/lib/articleCovers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
};

// Simple, safe markdown-lite renderer (headings, bold, italic, paragraphs, lists, links)
const renderContent = (raw) => {
  if (!raw) return null;
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    if (trimmed.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: trimmed.slice(4) });
    } else if (trimmed.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: trimmed.slice(3) });
    } else if (trimmed.startsWith("# ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h1", text: trimmed.slice(2) });
    } else if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph();
      if (!list) list = { type: "ul", items: [] };
      list.items.push(trimmed.replace(/^[-*]\s+/, ""));
    } else if (trimmed.startsWith("> ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "blockquote", text: trimmed.slice(2) });
    } else {
      flushList();
      paragraph.push(trimmed);
    }
  }
  flushParagraph();
  flushList();

  const inline = (text) => {
    // escape HTML then apply bold / italic / links
    const escape = (s) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    let s = escape(text);
    s = s.replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, (_m, l, u) =>
      `<a href="${u}" target="_blank" rel="noopener noreferrer" class="underline decoration-[#B08A4A] underline-offset-4 hover:text-[#2E5AA5]">${l}</a>`,
    );
    s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
    return s;
  };

  return blocks.map((b, idx) => {
    if (b.type === "h1")
      return (
        <h2 key={idx} className="font-serif text-3xl md:text-4xl text-[#0A2540] mt-12 mb-4 leading-tight">
          {b.text}
        </h2>
      );
    if (b.type === "h2")
      return (
        <h3 key={idx} className="font-serif text-2xl md:text-3xl text-[#0A2540] mt-10 mb-3 leading-tight">
          {b.text}
        </h3>
      );
    if (b.type === "h3")
      return (
        <h4 key={idx} className="font-serif text-xl md:text-2xl text-[#0A2540] mt-8 mb-2 leading-tight">
          {b.text}
        </h4>
      );
    if (b.type === "blockquote")
      return (
        <blockquote
          key={idx}
          className="my-6 border-l-2 border-[#B08A4A] pl-6 font-serif italic text-xl text-[#0A2540]/85"
          dangerouslySetInnerHTML={{ __html: inline(b.text) }}
        />
      );
    if (b.type === "ul")
      return (
        <ul key={idx} className="my-4 space-y-2 pl-6 list-disc marker:text-[#B08A4A]">
          {b.items.map((it, i) => (
            <li
              key={i}
              className="font-sans text-base text-[#0A2540] leading-[1.85]"
              dangerouslySetInnerHTML={{ __html: inline(it) }}
            />
          ))}
        </ul>
      );
    return (
      <p
        key={idx}
        className="my-4 font-sans text-base md:text-lg text-[#0A2540]/90 leading-[1.85]"
        dangerouslySetInnerHTML={{ __html: inline(b.text) }}
      />
    );
  });
};

export default function HealthNoteDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading");
  usePageTitle(article?.title ? `${article.title} · Health Notes` : "Health Notes");

  const articleCover = article ? coverForArticle(article) : undefined;
  useOpenGraph({
    title: article?.title,
    description: article?.excerpt,
    url: article ? `https://mumbaielitediagnostics.com/health-notes/${article.slug}` : undefined,
    image: articleCover,
    type: "article",
    imageAlt: article?.title,
  });

  // Inject MedicalWebPage JSON-LD when article loads; cleanup on unmount / slug change
  useEffect(() => {
    if (!article) return;
    const site = "https://mumbaielitediagnostics.com";
    const url = `${site}/health-notes/${article.slug}`;
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      headline: article.title,
      description: article.excerpt,
      inLanguage: "en-IN",
      datePublished: article.created_at,
      dateModified: article.updated_at,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url,
      image: article.cover_image || DOCTOR.photo,
      author: {
        "@type": "Physician",
        name: article.author || DOCTOR.name,
        jobTitle: "Consultant Pathologist (Gold Medalist)",
        medicalSpecialty: "Pathology",
      },
      publisher: {
        "@type": "Organization",
        name: BRAND.name,
        logo: {
          "@type": "ImageObject",
          url: DOCTOR.photo,
        },
      },
      about: (article.tags || []).map((t) => ({ "@type": "Thing", name: t })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-schema", "medical-webpage");
    el.text = JSON.stringify({
      ...schema,
      image: articleCover || DOCTOR.photo,
    });
    document.head.appendChild(el);
    return () => {
      el.parentNode && el.parentNode.removeChild(el);
    };
  }, [article]);

  useEffect(() => {
    axios
      .get(`${API}/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        setState("ok");
      })
      .catch(() => setState("not_found"));
  }, [slug]);

  if (state === "loading")
    return (
      <div
        data-testid="article-loading"
        className="min-h-[50vh] flex items-center justify-center font-mono text-xs uppercase tracking-widest-plus text-[#94A3B8]"
      >
        Loading article…
      </div>
    );
  if (state === "not_found")
    return (
      <div
        data-testid="article-not-found"
        className="max-w-3xl mx-auto py-24 text-center px-6"
      >
        <h1 className="font-serif text-4xl text-[#0A2540] mb-4">Article not found</h1>
        <p className="font-sans text-base text-[#475569] mb-8">
          This article may have been unpublished or moved.
        </p>
        <Link to="/health-notes" className="btn-secondary">
          ← All Health Notes
        </Link>
      </div>
    );

  return (
    <article data-testid="article-detail" className="bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-12 pb-6">
        <Link
          to="/health-notes"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] mb-10"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Health Notes
        </Link>

        <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={12} strokeWidth={1.5} />
            {fmtDate(article.created_at)}
          </span>
        </div>

        <h1
          data-testid="article-title"
          className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#0A2540] leading-[1.05] tracking-tight"
        >
          {article.title}
        </h1>
        <p
          data-testid="article-excerpt"
          className="mt-6 font-serif italic text-xl md:text-2xl text-[#475569] leading-snug"
        >
          {article.excerpt}
        </p>

        <div
          data-testid="article-author-byline"
          className="mt-8 flex items-center gap-4 pb-8 border-b border-[#E2E8F0]"
        >
          <img
            src={DOCTOR.photo}
            alt={article.author || DOCTOR.name}
            data-testid="article-author-avatar"
            className="w-14 h-14 rounded-full object-cover border border-[#B08A4A]/40 shrink-0"
            loading="lazy"
          />
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest-plus text-[#94A3B8]">
              Written by
            </div>
            <div className="font-serif text-lg text-[#0A2540] leading-tight">
              {article.author || DOCTOR.name}
            </div>
            <div className="font-sans text-xs text-[#475569] leading-tight mt-0.5">
              {DOCTOR.credentials} · Consultant Pathologist
            </div>
          </div>
        </div>
      </div>

      {articleCover && (
        <div className="max-w-5xl mx-auto px-6 lg:px-8 mb-8 mt-2">
          <div className="aspect-[16/9] w-full overflow-hidden bg-[#F1F5F9]">
            <img
              src={articleCover}
              alt={article.title}
              className="w-full h-full object-cover"
              data-testid="article-cover-image"
            />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-16 pt-2">
        <div data-testid="article-content" className="prose-clean">
          {renderContent(article.content)}
        </div>

        <div className="mt-14 pt-6 border-t border-[#E2E8F0]">
          {/* Primary — Teleconsultation / Report review */}
          <div
            data-testid="article-teleconsult-cta"
            className="bg-white border-2 border-[#0A2540] p-8 md:p-10 lg:p-12 relative overflow-hidden mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3 inline-flex items-center gap-2">
                  <Stethoscope size={12} strokeWidth={1.5} />
                  Second Opinion · Report Review
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-3 text-[#0A2540]">
                  Confused About Your Blood Reports or Dengue CBC?
                </h3>
                <p className="font-sans text-sm md:text-base text-[#475569] leading-relaxed max-w-xl">
                  Get your lab reports and CBC trends reviewed directly by{" "}
                  <span className="text-[#0A2540]">
                    Dr. Anubhuti Jain (MD Pathology, NUS Observer)
                  </span>
                  . Secure, private, and returned with a clinician&rsquo;s
                  interpretation — not just a lab reading.
                </p>
              </div>
              <div className="md:col-span-4">
                <a
                  href={teleconsultUrl(TELECONSULT.reportReviewMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="article-cta-teleconsult"
                  className="group inline-flex items-center justify-center gap-3 w-full bg-[#0A2540] text-white hover:bg-[#B08A4A] px-6 py-4 text-xs tracking-widest-plus uppercase transition-all font-semibold"
                >
                  <Video size={16} strokeWidth={1.5} />
                  Book Report Review via WhatsApp
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>

          {/* Secondary — Local lab / home collection */}
          <div
            data-testid="article-local-cta"
            className="bg-[#0A2540] text-white p-8 md:p-10 lg:p-12 relative overflow-hidden"
          >
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#B08A4A]/15 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3">
                  In Gwalior · Same-Day Diagnostics
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-3">
                  Need a Dengue Profile or CBC Test in Gwalior?
                </h3>
                <p className="font-sans text-sm md:text-base text-white/75 leading-relaxed max-w-xl">
                  Book home sample collection across Vinay Nagar, Lashkar,
                  City Centre, Thatipur, and Morar — or visit our Vinay Nagar
                  centre. Reports are personally reviewed by a Gold Medalist
                  pathologist.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-white/70">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={12} strokeWidth={1.5} className="text-[#B08A4A]" />
                    Vinay Nagar, Gwalior
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Home size={12} strokeWidth={1.5} className="text-[#B08A4A]" />
                    Home collection available
                  </span>
                </div>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3">
                <a
                  href={whatsappUrl(
                    "Hello, I would like to book a home sample collection / diagnostic test in Gwalior with Mumbai Elite Diagnostics. Please share available slots.",
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="article-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-3 bg-white text-[#0A2540] hover:bg-[#B08A4A] hover:text-white px-6 py-4 text-xs tracking-widest-plus uppercase transition-all font-semibold"
                >
                  <MessageCircle size={16} strokeWidth={1.5} />
                  Book Home Collection
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
                <Link
                  to="/contact"
                  data-testid="article-cta-visit"
                  className="inline-flex items-center justify-center gap-3 bg-transparent text-white border border-white/40 hover:border-white hover:bg-white/10 px-6 py-4 text-xs tracking-widest-plus uppercase transition-all"
                >
                  Visit Vinay Nagar Centre
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {article.tags?.length > 0 && (
          <div className="mt-14 pt-6 border-t border-[#E2E8F0] flex flex-wrap items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] inline-flex items-center gap-1.5">
              <Tag size={12} strokeWidth={1.5} />
              Tags
            </span>
            {article.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] border border-[#E2E8F0] px-3 py-1"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-14 pt-6 border-t border-[#E2E8F0] text-center">
          <Link
            to="/health-notes"
            data-testid="article-all-link"
            className="btn-secondary"
          >
            All Health Notes
          </Link>
        </div>
      </div>
    </article>
  );
}
